import {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  Link,
  useLocation,
} from 'react-router-dom'

import {
  adminNavigation,
} from '../../config/adminNavigation'

import {
  AdminIcon,
} from './AdminIcon'

type AdminSidebarProps = {
  collapsed: boolean
  mobileOpen: boolean
  onCloseMobile: () => void
}

export function AdminSidebar({
  collapsed,
  mobileOpen,
  onCloseMobile,
}: AdminSidebarProps) {
  const location = useLocation()

  const [search, setSearch] =
    useState('')

  const [openGroups, setOpenGroups] =
    useState<Record<string, boolean>>(() => {
      const initial: Record<string, boolean> = {}

      adminNavigation.forEach((group) => {
        initial[group.id] = true
      })

      return initial
    })

  useEffect(() => {
    onCloseMobile()
  }, [
    location.pathname,
    onCloseMobile,
  ])

  const normalizedSearch =
    search.trim().toLowerCase()

  const groups = useMemo(() => {
    if (!normalizedSearch) {
      return adminNavigation
    }

    return adminNavigation
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => {
            const haystack = [
              item.title,
              item.description,
              group.title,
            ]
              .join(' ')
              .toLowerCase()

            return haystack.includes(
              normalizedSearch,
            )
          },
        ),
      }))
      .filter(
        (group) =>
          group.items.length > 0,
      )
  }, [normalizedSearch])

  function toggleGroup(
    groupId: string,
  ) {
    setOpenGroups((current) => ({
      ...current,
      [groupId]:
        !current[groupId],
    }))
  }

  function isActive(
    href: string,
  ) {
    if (href === '/admin/') {
      return (
        location.pathname === '/admin' ||
        location.pathname === '/admin/'
      )
    }

    return (
      location.pathname === href ||
      location.pathname.startsWith(
        `${href}/`,
      )
    )
  }

  return (
    <>
      <div
        className={[
          'admin-sidebar-overlay',
          mobileOpen
            ? 'is-visible'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={onCloseMobile}
      />

      <aside
        className={[
          'admin-sidebar',
          collapsed
            ? 'is-collapsed'
            : '',
          mobileOpen
            ? 'is-mobile-open'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="admin-sidebar__brand">
          <div className="admin-sidebar__brand-mark">
            K
          </div>

          {!collapsed && (
            <div className="admin-sidebar__brand-text">
              <strong>
                Kraken
              </strong>

              <span>
                Administration
              </span>
            </div>
          )}

          <button
            type="button"
            className="admin-sidebar__mobile-close"
            onClick={onCloseMobile}
            aria-label="Закрыть меню"
          >
            <AdminIcon
              name="close"
              size={20}
            />
          </button>
        </div>

        {!collapsed && (
          <div className="admin-sidebar__search">
            <AdminIcon
              name="search"
              size={17}
            />

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value,
                )
              }
              placeholder="Найти раздел..."
              autoComplete="off"
            />
          </div>
        )}

        <nav className="admin-sidebar__navigation">
          <Link to="/admin/emojis" onClick={onCloseMobile} title="Эмодзи и стикеры" style={{display:'block',padding:'12px 18px',color:'#a9d5ff',textDecoration:'none',fontSize:13}}>{collapsed?'✦':'✦ Эмодзи и стикеры'}</Link>
          <Link
            to="/admin/"
            className={[
              'admin-sidebar__dashboard',
              isActive('/admin/')
                ? 'is-active'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            title={
              collapsed
                ? 'Обзор'
                : undefined
            }
          >
            <span className="admin-sidebar__item-icon">
              <AdminIcon
                name="dashboard"
              />
            </span>

            {!collapsed && (
              <span>
                Обзор
              </span>
            )}
          </Link>

          {groups.map((group) => {
            const groupOpen =
              normalizedSearch
                ? true
                : openGroups[group.id]

            return (
              <div
                className="admin-sidebar__group"
                key={group.id}
              >
                {!collapsed && (
                  <button
                    type="button"
                    className="admin-sidebar__group-title"
                    onClick={() =>
                      toggleGroup(
                        group.id,
                      )
                    }
                  >
                    <span>
                      {group.title}
                    </span>

                    <AdminIcon
                      name="chevron"
                      size={14}
                      className={
                        groupOpen
                          ? 'is-open'
                          : ''
                      }
                    />
                  </button>
                )}

                {(collapsed ||
                  groupOpen) && (
                  <div className="admin-sidebar__items">
                    {group.items.map(
                      (item) => {
                        const active =
                          isActive(
                            item.href,
                          )

                        const content = (
                          <>
                            <span className="admin-sidebar__item-icon">
                              <AdminIcon
                                name={
                                  item.icon
                                }
                              />
                            </span>

                            {!collapsed && (
                              <>
                                <span className="admin-sidebar__item-label">
                                  {
                                    item.title
                                  }
                                </span>

                                {item.legacy && (
                                  <span
                                    className="admin-sidebar__legacy"
                                    title="Старая страница"
                                  >
                                    <AdminIcon
                                      name="external"
                                      size={12}
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </>
                        )

                        if (
                          item.legacy
                        ) {
                          return (
                            <a
                              key={
                                item.href
                              }
                              href={
                                item.href
                              }
                              title={
                                collapsed
                                  ? item.title
                                  : undefined
                              }
                              className={[
                                'admin-sidebar__item',
                                active
                                  ? 'is-active'
                                  : '',
                              ]
                                .filter(
                                  Boolean,
                                )
                                .join(' ')}
                            >
                              {content}
                            </a>
                          )
                        }

                        return (
                          <Link
                            key={
                              item.href
                            }
                            to={
                              item.href
                            }
                            title={
                              collapsed
                                ? item.title
                                : undefined
                            }
                            className={[
                              'admin-sidebar__item',
                              active
                                ? 'is-active'
                                : '',
                            ]
                              .filter(
                                Boolean,
                              )
                              .join(' ')}
                          >
                            {content}
                          </Link>
                        )
                      },
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {!collapsed &&
            groups.length === 0 && (
              <div className="admin-sidebar__empty">
                Ничего не найдено
              </div>
            )}
        </nav>

        <div className="admin-sidebar__footer">
          <a
            href="/"
            className="admin-sidebar__back"
            title={
              collapsed
                ? 'Вернуться в Академию'
                : undefined
            }
          >
            <AdminIcon
              name="home"
              size={19}
            />

            {!collapsed && (
              <span>
                Вернуться в Академию
              </span>
            )}
          </a>
        </div>
      </aside>
    </>
  )
}
